import { useState, useEffect } from 'react';
import { useCurrentUser } from '@/shared/hooks/useCurrentUser';
import type { InventoryEditFormValues, InventoryEditLookups } from '../types/inventoryEdit.types';
import { inventoryEditService } from '../services/inventoryEdit.service';

const defaultValues: InventoryEditFormValues = {
  id: '',
  code: '',
  name: '',
  category_name: '',
  unit: '',
  specification: '',
  item_type: 'main',
  supplier_name: '',
  origin_name: '',
  manufacturer_code: '',
  unit_price: 0,
  opening_stock: 0,
  minimum_stock: 0,
  warehouse_id: '',
  storage_location: '',
  note: '',
  is_stock_tracked: true,
  height: '',
  canopy_diameter: '',
  pot_spec: '',
  substrate: '',
  water_requirement: '',
  light_requirement: '',
  seasonality: '',
  expiry_date: '',
};

export function useInventoryEdit(id: string) {
  const { user } = useCurrentUser();
  const [values, setValues] = useState<InventoryEditFormValues>(defaultValues);
  const [lookups, setLookups] = useState<InventoryEditLookups>();
  const [imagePreview, setImagePreview] = useState<string>('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    async function init() {
      if (!id || !user.companyId) {
        if (id && !user.companyId) setIsLoading(false); // No companyId, stop loading
        return;
      }
      try {
        const [itemData, lookupData] = await Promise.all([
          inventoryEditService.getInventoryItem(id, user.companyId),
          inventoryEditService.getLookups(user.companyId),
        ]);

        if (itemData) {
          const specs = itemData.specifications || {};
          setValues({
            ...itemData,
            category_name: itemData.category_name || '',
            unit: itemData.unit || '',
            specification: itemData.specification || '',
            supplier_name: itemData.supplier_name || '',
            origin_name: itemData.origin_name || '',
            manufacturer_code: itemData.manufacturer_code || '',
            warehouse_id: itemData.warehouse_id || '',
            storage_location: itemData.storage_location || '',
            note: itemData.note || '',
            // Unpack specs
            height: specs.height || '',
            canopy_diameter: specs.canopy_diameter || '',
            pot_spec: specs.pot_spec || '',
            substrate: specs.substrate || '',
            water_requirement: specs.water_requirement || '',
            light_requirement: specs.light_requirement || '',
            seasonality: specs.seasonality || '',
            expiry_date: specs.expiry_date || '',
          });
          setImagePreview(itemData.thumbnail_url || '');
        }
        if (lookupData) {
          setLookups(lookupData);
        }
      } catch (error) {
        console.error('[useInventoryEdit] init error:', error);
      } finally {
        setIsLoading(false);
      }
    }
    init();
  }, [id, user.companyId]);

  const updateItem = async () => {
    if (!user.companyId || !user.id) return;
    setIsSaving(true);
    try {
      let thumbnailUrl = imagePreview;
      if (imageFile) {
        thumbnailUrl = await inventoryEditService.uploadImage(imageFile, user.companyId) || '';
      }

      await inventoryEditService.updateInventoryItem(
        id,
        { 
          ...values, 
          thumbnail_url: thumbnailUrl,
          // Pack specs
          specifications: {
            height: values.height,
            canopy_diameter: values.canopy_diameter,
            pot_spec: values.pot_spec,
            substrate: values.substrate,
            water_requirement: values.water_requirement,
            light_requirement: values.light_requirement,
            seasonality: values.seasonality,
            expiry_date: values.expiry_date,
          }
        } as any,
        user.id,
        user.companyId
      );
      return true;
    } catch (error) {
      console.error('[useInventoryEdit] updateItem error:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const stockValue = values.opening_stock * values.unit_price;

  return {
    values,
    setValues,
    lookups,
    imagePreview,
    setImagePreview,
    setImageFile,
    isLoading,
    isSaving,
    updateItem,
    stockValue,
  };
}
